import type { Schema } from '@dealdesk/api-schema';

export type ApiProblem = Schema<'ApiProblem'>;
export type FieldError = NonNullable<ApiProblem['fieldErrors']>[number];

/**
 * Real-API codes (ErrorCode, UPPER_SNAKE) that mean the same as a legacy mock code the UI already handles. After
 * lower-casing, these map onto the legacy spelling so `error.code === 'validation'` and the `errors.server.<code>`
 * messages work the same against the mock and the real API.
 */
const CODE_ALIASES: Readonly<Record<string, string>> = {
  validation_error: 'validation',
  unauthenticated: 'unauthorized',
  session_expired: 'unauthorized',
  invalid_transition: 'invalid_state',
  self_change_forbidden: 'self_change',
  account_locked: 'locked',
  config_version_required: 'no_configuration',
  invalid_mfa_code: 'invalid_code',
};

/**
 * One form for every error code: lower snake case, with real-API synonyms folded onto the legacy mock codes.
 * "VERSION_CONFLICT" and "version_conflict" both become "version_conflict"; "VALIDATION_ERROR" becomes "validation".
 */
export function normalizeErrorCode(code: string | null | undefined): string {
  const lower = (code ?? '').trim().toLowerCase();
  return CODE_ALIASES[lower] ?? lower;
}

/**
 * Every non-2xx response becomes an ApiError. Components never inspect raw responses.
 * status 0 means the request never reached the server (offline, DNS, CORS).
 * `code` is normalised (normalizeErrorCode); `rawCode` keeps what the server sent.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly rawCode: string;
  readonly traceId: string;
  readonly fieldErrors: readonly FieldError[];
  readonly details: Record<string, unknown>;

  constructor(problem: ApiProblem) {
    super(problem.message);
    this.name = 'ApiError';
    this.status = problem.status;
    this.rawCode = problem.code;
    this.code = normalizeErrorCode(problem.code);
    this.traceId = problem.traceId;
    this.fieldErrors = problem.fieldErrors ?? [];
    this.details = problem.details ?? {};
  }

  /** A text value from `details` (e.g. who changed a record), or '' when absent or not text. */
  detailText(key: string): string {
    const v = this.details[key];
    return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
  }

  get isNetwork() {
    return this.status === 0;
  }
  get isUnauthorized() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isConflict() {
    return this.status === 409;
  }
  /** 422: validation or business rule failure. */
  get isUnprocessable() {
    return this.status === 422;
  }
  get isServer() {
    return this.status >= 500;
  }
  /** 409 caused by a stale If-Match version (as opposed to a state conflict). */
  get isVersionConflict() {
    return this.status === 409 && this.code === 'version_conflict';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

function isProblem(body: unknown): body is ApiProblem {
  return typeof body === 'object' && body !== null && 'status' in body && 'code' in body && 'message' in body;
}

export function toApiError(error: unknown, response?: Response): ApiError {
  if (error instanceof ApiError) return error;
  if (isProblem(error)) return new ApiError({ ...error, traceId: error.traceId || response?.headers.get('x-trace-id') || 'unknown' });
  if (response) {
    return new ApiError({
      status: response.status,
      code: `http_${response.status}`,
      message: response.statusText || 'Request failed',
      traceId: response.headers.get('x-trace-id') ?? 'unknown',
    });
  }
  return new ApiError({ status: 0, code: 'network', message: 'Network request failed', traceId: 'none' });
}
